import { AxiosResponse } from "axios";
import axiosClient from "@/services/api/index";

export interface ActionButton {
  label?: string;
  destination: "home" | "map" | "document";
}

export interface ResidentialContent {
  brandText?: string;
  headline?: string;
  description?: string;
  actionButtons?: ActionButton[];
  logoImageUrl?: string;
}

export interface ProjectPageAsset {
  fileName: string;
  fileUrl: string;
  mimetype: string;
}

export interface LandSubdivisionProjectDocument extends ProjectPageAsset {
  id: string;
  name: string;
  description?: string;
}

export interface LandSubdivisionContent {
  projectName?: string;
  heroButtonColor?: string;
  projectLogoUrl?: string;
  projectLogoFileName?: string;
  backgroundImageFileName?: string;
  agency?: {
    name?: string;
    logoUrl?: string;
    logoFileName?: string;
    brandColor?: string;
  };
  aboutHeading?: string;
  aboutDescription?: string;
  features?: string[];
  documents?: LandSubdivisionProjectDocument[];
  contact?: {
    name?: string;
    title?: string;
    phone?: string;
    email?: string;
    photoUrl?: string;
    photoFileName?: string;
  };
  locationAddress?: string;
  callToAction?: {
    heading?: string;
    subtext?: string;
    buttonText?: string;
    footerColor?: string;
  };
}

export interface ProjectPageData {
  _id?: string;
  estateId?: string;
  estateType?: string;
  backgroundImageUrl?: string | null;
  slug?: string;
  shortCode?: string;
  residentialContent?: ResidentialContent;
  landSubContent?: LandSubdivisionContent;
}

export default {
  getByEstateId(estateId: string): Promise<AxiosResponse<ProjectPageData>> {
    return axiosClient.get(`project-page/${estateId}`);
  },

  getByEstateIdPublic(
    estateId: string
  ): Promise<AxiosResponse<ProjectPageData>> {
    return axiosClient.get(`project-page/public/estate/${estateId}`);
  },

  getBySlug(slug: string): Promise<AxiosResponse<ProjectPageData>> {
    return axiosClient.get(`project-page/public/${slug}`);
  },

  getByShortCode(shortCode: string): Promise<AxiosResponse<ProjectPageData>> {
    return axiosClient.get(`project-page/public/short/${shortCode}`);
  },

  upsert(
    estateId: string,
    estateType: string,
    data: {
      residentialContent?: ResidentialContent;
      landSubContent?: LandSubdivisionContent;
    }
  ): Promise<AxiosResponse<ProjectPageData>> {
    return axiosClient.put(`project-page/${estateId}/${estateType}`, data);
  },

  uploadBackground(
    estateId: string,
    estateType: string,
    file: File
  ): Promise<AxiosResponse<ProjectPageData>> {
    const fd = new FormData();

    fd.append("file", file);
    return axiosClient.post(
      `project-page/${estateId}/${estateType}/background`,
      fd,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
  },

  uploadLogo(
    estateId: string,
    estateType: string,
    file: File
  ): Promise<AxiosResponse<ProjectPageData>> {
    const fd = new FormData();

    fd.append("file", file);
    return axiosClient.post(`project-page/${estateId}/${estateType}/logo`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  uploadLandSubdivisionAsset(
    estateId: string,
    assetType:
      | "hero-image"
      | "project-logo"
      | "agency-logo"
      | "contact-photo"
      | "document",
    file: File
  ): Promise<AxiosResponse<ProjectPageAsset>> {
    const fd = new FormData();

    fd.append("file", file);
    return axiosClient.post(
      `project-page/${estateId}/land-subdivision/assets/${assetType}`,
      fd,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
  },
};
